/**
 * Performance Optimizer for API Requests
 * 
 * Features:
 * - Request batching and debouncing
 * - Performance monitoring
 * - Slow endpoint detection
 * - Automatic optimization strategies
 */

import { cacheManager as defaultCacheManager, CacheManager } from './cache-manager';
import { AxiosResponse } from 'axios';

export interface PerformanceMetrics {
  endpoint: string;
  method: string;
  avgResponseTime: number;
  minResponseTime: number;
  maxResponseTime: number;
  requestCount: number;
  errorCount: number;
  errorRate: number;
  cacheHitRate: number;
  lastCalled: number;
  throughput: number; // requests per second
}

export interface BatchRequestConfig {
  batchKey: string;
  maxBatchSize: number;
  maxWaitTime: number; // milliseconds
  requestTransformer?: (requests: unknown[]) => unknown;
  responseTransformer?: (response: unknown, originalRequests: unknown[]) => unknown[];
}

export interface OptimizationStrategy {
  name: string;
  condition: (metrics: PerformanceMetrics) => boolean;
  action: (endpoint: string, metrics: PerformanceMetrics) => Promise<void>;
}

export interface SlowEndpointAlert {
  endpoint: string;
  method: string;
  avgResponseTime: number;
  threshold: number;
  timestamp: number;
  suggestions: string[];
}

const SLOW_ENDPOINT_THRESHOLD = 2000; // 2 seconds
const HIGH_ERROR_RATE_THRESHOLD = 0.1; // 10%
const METRICS_RETENTION_DAYS = 7;

interface QueuedBatchRequest {
  request: unknown;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

function isFulfilledResult(value: unknown): value is PromiseFulfilledResult<unknown> {
  return typeof value === 'object'
    && value !== null
    && 'status' in value
    && value.status === 'fulfilled'
    && 'value' in value;
}

function isRejectedResult(value: unknown): value is PromiseRejectedResult {
  return typeof value === 'object'
    && value !== null
    && 'status' in value
    && value.status === 'rejected'
    && 'reason' in value;
}

export class PerformanceOptimizer {
  private metrics = new Map<string, PerformanceMetrics>();
  private batchQueues = new Map<string, {
    requests: QueuedBatchRequest[];
    timer: NodeJS.Timeout | null;
    config: BatchRequestConfig;
  }>();
  private optimizationStrategies: OptimizationStrategy[] = [];
  private slowEndpointAlerts: SlowEndpointAlert[] = [];
  private cacheManager: CacheManager;

  constructor(cacheManager?: CacheManager) {
    this.cacheManager = cacheManager || defaultCacheManager;
    this.initializeOptimizationStrategies();
    
    // Set up periodic analysis
    setInterval(() => this.analyzePerformance(), 60 * 1000); // Every minute
    setInterval(() => this.cleanupOldMetrics(), 24 * 60 * 60 * 1000); // Daily
  }

  /**
   * Monitor and measure API request performance
   */
  async measureRequest<T>(
    endpoint: string,
    method: string,
    requestFn: () => Promise<AxiosResponse<T>>,
    options: {
      enableCaching?: boolean;
      cacheKey?: string;
      cacheTTL?: number;
      tags?: string[];
    } = {}
  ): Promise<AxiosResponse<T>> {
    const startTime = Date.now();
    const metricKey = `${method}:${endpoint}`;

    try {
      // Check cache first if enabled
      if (options.enableCaching && options.cacheKey) {
        const cached = await this.cacheManager.get(options.cacheKey);
        if (cached) {
          this.recordCacheHit(metricKey);
          return {
            data: cached,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: {},
          } as AxiosResponse<T>;
        }
      }

      // Execute request
      const response = await requestFn();
      const responseTime = Date.now() - startTime;

      // Cache response if enabled
      if (options.enableCaching && options.cacheKey && response.status >= 200 && response.status < 300) {
        await this.cacheManager.set(options.cacheKey, response.data, {
          ttl: options.cacheTTL,
          tags: options.tags,
        });
      }

      // Record metrics
      this.recordRequestMetrics(metricKey, responseTime, false);

      return response;
    } catch (error) {
      const responseTime = Date.now() - startTime;
      this.recordRequestMetrics(metricKey, responseTime, true);
      throw error;
    }
  }

  /**
   * Batch multiple requests together
   */
  async batchRequest<T>(
    config: BatchRequestConfig,
    request: unknown
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const { batchKey, maxBatchSize, maxWaitTime } = config;
      
      let batch = this.batchQueues.get(batchKey);
      
      if (!batch) {
        batch = {
          requests: [],
          timer: null,
          config,
        };
        this.batchQueues.set(batchKey, batch);
      }

      // Add request to batch
      batch.requests.push({
        request,
        resolve: (value) => resolve(value as T),
        reject,
      });

      // Clear existing timer
      if (batch.timer) {
        clearTimeout(batch.timer);
      }

      // Process batch if it's full or set timer for max wait time
      if (batch.requests.length >= maxBatchSize) {
        this.processBatch(batchKey);
      } else {
        batch.timer = setTimeout(() => {
          this.processBatch(batchKey);
        }, maxWaitTime);
      }
    });
  }

  /**
   * Debounce requests to the same endpoint
   */
  debounceRequest<T>(
    key: string,
    requestFn: () => Promise<T>,
    delay: number = 300
  ): Promise<T> {
    const existing = this.batchQueues.get(`debounce:${key}`);
    
    if (existing && existing.timer) {
      clearTimeout(existing.timer);
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(async () => {
        try {
          const result = await requestFn();
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          this.batchQueues.delete(`debounce:${key}`);
        }
      }, delay);

      this.batchQueues.set(`debounce:${key}`, {
        requests: [{
          request: key,
          resolve: (value) => resolve(value as T),
          reject,
        }],
        timer,
        config: {} as BatchRequestConfig,
      });
    });
  }

  /**
   * Get performance metrics for an endpoint
   */
  getMetrics(endpoint?: string): PerformanceMetrics | PerformanceMetrics[] {
    if (endpoint) {
      return this.metrics.get(endpoint) || this.createEmptyMetrics(endpoint);
    }
    
    return Array.from(this.metrics.values());
  }

  /**
   * Get slow endpoints that need optimization
   */
  getSlowEndpoints(threshold: number = SLOW_ENDPOINT_THRESHOLD): PerformanceMetrics[] {
    return Array.from(this.metrics.values())
      .filter(metric => metric.avgResponseTime > threshold)
      .sort((a, b) => b.avgResponseTime - a.avgResponseTime);
  }

  /**
   * Get endpoints with high error rates
   */
  getHighErrorRateEndpoints(threshold: number = HIGH_ERROR_RATE_THRESHOLD): PerformanceMetrics[] {
    return Array.from(this.metrics.values())
      .filter(metric => metric.errorRate > threshold && metric.requestCount > 10)
      .sort((a, b) => b.errorRate - a.errorRate);
  }

  /**
   * Get recent slow endpoint alerts
   */
  getSlowEndpointAlerts(since?: number): SlowEndpointAlert[] {
    const cutoff = since || Date.now() - (24 * 60 * 60 * 1000); // Last 24 hours
    return this.slowEndpointAlerts.filter(alert => alert.timestamp > cutoff);
  }

  /**
   * Generate performance report
   */
  generatePerformanceReport(): {
    summary: {
      totalEndpoints: number;
      slowEndpoints: number;
      highErrorRateEndpoints: number;
      avgResponseTime: number;
      totalRequests: number;
      cacheHitRate: number;
    };
    slowEndpoints: PerformanceMetrics[];
    highErrorRateEndpoints: PerformanceMetrics[];
    recentAlerts: SlowEndpointAlert[];
    recommendations: string[];
  } {
    const allMetrics = Array.from(this.metrics.values());
    const slowEndpoints = this.getSlowEndpoints();
    const highErrorRateEndpoints = this.getHighErrorRateEndpoints();
    
    const totalRequests = allMetrics.reduce((sum, metric) => sum + metric.requestCount, 0);
    const avgResponseTime = allMetrics.length > 0 
      ? allMetrics.reduce((sum, metric) => sum + metric.avgResponseTime, 0) / allMetrics.length
      : 0;
    
    const totalCacheRequests = allMetrics.reduce((sum, metric) => 
      sum + metric.requestCount + (metric.cacheHitRate * metric.requestCount), 0
    );
    const totalCacheHits = allMetrics.reduce((sum, metric) => 
      sum + (metric.cacheHitRate * metric.requestCount), 0
    );
    const overallCacheHitRate = totalCacheRequests > 0 ? totalCacheHits / totalCacheRequests : 0;

    return {
      summary: {
        totalEndpoints: allMetrics.length,
        slowEndpoints: slowEndpoints.length,
        highErrorRateEndpoints: highErrorRateEndpoints.length,
        avgResponseTime,
        totalRequests,
        cacheHitRate: overallCacheHitRate,
      },
      slowEndpoints: slowEndpoints.slice(0, 10), // Top 10
      highErrorRateEndpoints: highErrorRateEndpoints.slice(0, 10), // Top 10
      recentAlerts: this.getSlowEndpointAlerts(),
      recommendations: this.generateRecommendations(slowEndpoints, highErrorRateEndpoints),
    };
  }

  /**
   * Add custom optimization strategy
   */
  addOptimizationStrategy(strategy: OptimizationStrategy): void {
    this.optimizationStrategies.push(strategy);
  }

  /**
   * Clear all performance data
   */
  clearMetrics(): void {
    this.metrics.clear();
    this.slowEndpointAlerts = [];
  }

  // Private methods

  private recordRequestMetrics(
    metricKey: string,
    responseTime: number,
    isError: boolean
  ): void {
    let metric = this.metrics.get(metricKey);
    
    if (!metric) {
      const [method = 'GET', endpoint = metricKey] = metricKey.split(':', 2);
      metric = this.createEmptyMetrics(endpoint, method);
    }

    // Update timing metrics
    metric.requestCount++;
    metric.lastCalled = Date.now();
    
    if (isError) {
      metric.errorCount++;
    }
    
    // Update response time statistics
    if (metric.requestCount === 1) {
      metric.avgResponseTime = responseTime;
      metric.minResponseTime = responseTime;
      metric.maxResponseTime = responseTime;
    } else {
      metric.avgResponseTime = (
        (metric.avgResponseTime * (metric.requestCount - 1)) + responseTime
      ) / metric.requestCount;
      metric.minResponseTime = Math.min(metric.minResponseTime, responseTime);
      metric.maxResponseTime = Math.max(metric.maxResponseTime, responseTime);
    }
    
    // Update error rate
    metric.errorRate = metric.errorCount / metric.requestCount;
    
    // Calculate throughput (requests per second over last minute)
    const oneMinuteAgo = Date.now() - 60000;
    if (metric.lastCalled > oneMinuteAgo) {
      // Simplified throughput calculation
      metric.throughput = metric.requestCount / ((Date.now() - metric.lastCalled + 60000) / 1000);
    }

    this.metrics.set(metricKey, metric);

    // Check for slow endpoint alert
    if (responseTime > SLOW_ENDPOINT_THRESHOLD && !isError) {
      this.checkSlowEndpointAlert(metricKey, responseTime);
    }
  }

  private recordCacheHit(metricKey: string): void {
    let metric = this.metrics.get(metricKey);
    
    if (!metric) {
      const [method = 'GET', endpoint = metricKey] = metricKey.split(':', 2);
      metric = this.createEmptyMetrics(endpoint, method);
    }

    // Update cache hit rate
    const totalRequests = metric.requestCount + 1; // Including this cache hit
    const currentCacheHits = metric.cacheHitRate * metric.requestCount;
    metric.cacheHitRate = (currentCacheHits + 1) / totalRequests;

    this.metrics.set(metricKey, metric);
  }

  private createEmptyMetrics(endpoint: string, method: string = 'GET'): PerformanceMetrics {
    return {
      endpoint,
      method,
      avgResponseTime: 0,
      minResponseTime: 0,
      maxResponseTime: 0,
      requestCount: 0,
      errorCount: 0,
      errorRate: 0,
      cacheHitRate: 0,
      lastCalled: 0,
      throughput: 0,
    };
  }

  private async processBatch(batchKey: string): Promise<void> {
    const batch = this.batchQueues.get(batchKey);
    if (!batch || batch.requests.length === 0) return;

    const { requests, config } = batch;
    this.batchQueues.delete(batchKey);

    try {
      // Transform individual requests into batch request
      const batchRequest = config.requestTransformer 
        ? config.requestTransformer(requests.map(r => r.request))
        : requests.map(r => r.request);
      void batchRequest;

      // Execute batch request (this would be implemented by the specific API)
      // For now, we'll execute requests individually
      const responses = await Promise.allSettled(
        requests.map(async (entry) => {
          // This is a simplified implementation
          // In a real batch system, you'd make a single API call
          return entry.request;
        })
      );

      // Transform batch response back to individual responses
      const individualResponses = config.responseTransformer
        ? config.responseTransformer(responses, requests.map(r => r.request))
        : responses;

      // Resolve individual promises
      requests.forEach((entry, index) => {
        const response = individualResponses[index];
        if (isFulfilledResult(response)) {
          entry.resolve(response.value);
        } else if (isRejectedResult(response)) {
          entry.reject(response.reason);
        } else if (response !== undefined) {
          entry.resolve(response);
        } else {
          entry.reject(new Error('Batch processing failed'));
        }
      });
    } catch (error) {
      // Reject all requests in the batch
      requests.forEach((entry) => entry.reject(error));
    }
  }

  private checkSlowEndpointAlert(metricKey: string, responseTime: number): void {
    const [method = 'GET', endpoint = metricKey] = metricKey.split(':', 2);
    
    // Check if we already have a recent alert for this endpoint
    const recentAlert = this.slowEndpointAlerts.find(
      alert => alert.endpoint === endpoint && 
               alert.method === method &&
               Date.now() - alert.timestamp < 300000 // 5 minutes
    );

    if (!recentAlert) {
      const suggestions = this.generateSlowEndpointSuggestions(endpoint, responseTime);
      
      this.slowEndpointAlerts.push({
        endpoint,
        method,
        avgResponseTime: responseTime,
        threshold: SLOW_ENDPOINT_THRESHOLD,
        timestamp: Date.now(),
        suggestions,
      });

      // Keep only last 100 alerts
      if (this.slowEndpointAlerts.length > 100) {
        this.slowEndpointAlerts = this.slowEndpointAlerts.slice(-100);
      }

      console.warn(`Slow endpoint detected: ${method} ${endpoint} (${responseTime}ms)`, suggestions);
    }
  }

  private generateSlowEndpointSuggestions(endpoint: string, responseTime: number): string[] {
    const suggestions: string[] = [];

    if (responseTime > 5000) {
      suggestions.push('Consider implementing pagination for large datasets');
      suggestions.push('Review database query performance and indexing');
    }

    if (responseTime > 3000) {
      suggestions.push('Implement response caching for this endpoint');
      suggestions.push('Consider using request debouncing or batching');
    }

    if (endpoint.includes('/search') || endpoint.includes('/filter')) {
      suggestions.push('Implement search result caching');
      suggestions.push('Consider using debounced search queries');
    }

    if (endpoint.includes('/list') || endpoint.includes('/index')) {
      suggestions.push('Implement pagination and lazy loading');
      suggestions.push('Cache list responses with appropriate TTL');
    }

    return suggestions;
  }

  private initializeOptimizationStrategies(): void {
    // Strategy: Auto-cache slow endpoints
    this.optimizationStrategies.push({
      name: 'auto-cache-slow-endpoints',
      condition: (metrics) => metrics.avgResponseTime > 1000 && metrics.requestCount > 5,
      action: async (endpoint, _metrics) => {
        console.log(`Auto-caching enabled for slow endpoint: ${endpoint}`);
        // Implementation would depend on the specific caching strategy
      },
    });

    // Strategy: Alert for high error rates
    this.optimizationStrategies.push({
      name: 'high-error-rate-alert',
      condition: (metrics) => metrics.errorRate > HIGH_ERROR_RATE_THRESHOLD && metrics.requestCount > 10,
      action: async (endpoint, metrics) => {
        console.error(`High error rate detected for ${endpoint}: ${(metrics.errorRate * 100).toFixed(1)}%`);
        // Could emit to error tracking service
      },
    });

    // Strategy: Suggest batching for frequently called endpoints
    this.optimizationStrategies.push({
      name: 'suggest-batching',
      condition: (metrics) => metrics.throughput > 10 && metrics.avgResponseTime < 500,
      action: async (endpoint, _metrics) => {
        console.log(`Consider implementing request batching for high-frequency endpoint: ${endpoint}`);
      },
    });
  }

  private async analyzePerformance(): Promise<void> {
    // Apply optimization strategies
    for (const [key, metrics] of this.metrics.entries()) {
      for (const strategy of this.optimizationStrategies) {
        if (strategy.condition(metrics)) {
          try {
            await strategy.action(key, metrics);
          } catch (error) {
            console.error(`Optimization strategy ${strategy.name} failed:`, error);
          }
        }
      }
    }
  }

  private generateRecommendations(
    slowEndpoints: PerformanceMetrics[],
    highErrorRateEndpoints: PerformanceMetrics[]
  ): string[] {
    const recommendations: string[] = [];

    if (slowEndpoints.length > 0) {
      recommendations.push(`${slowEndpoints.length} endpoints are performing slowly. Consider implementing caching.`);
      
      if (slowEndpoints.some(e => e.endpoint.includes('/list'))) {
        recommendations.push('List endpoints are slow. Implement pagination and optimize queries.');
      }
      
      if (slowEndpoints.some(e => e.endpoint.includes('/search'))) {
        recommendations.push('Search endpoints are slow. Implement search result caching and debouncing.');
      }
    }

    if (highErrorRateEndpoints.length > 0) {
      recommendations.push(`${highErrorRateEndpoints.length} endpoints have high error rates. Review error handling and retry logic.`);
    }

    const cacheableEndpoints = Array.from(this.metrics.values())
      .filter(m => m.requestCount > 10 && m.cacheHitRate < 0.3 && m.errorRate < 0.05);
      
    if (cacheableEndpoints.length > 0) {
      recommendations.push(`${cacheableEndpoints.length} endpoints could benefit from improved caching strategies.`);
    }

    return recommendations;
  }

  private cleanupOldMetrics(): void {
    const cutoff = Date.now() - (METRICS_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    
    for (const [key, metric] of this.metrics.entries()) {
      if (metric.lastCalled < cutoff) {
        this.metrics.delete(key);
      }
    }

    // Clean up old alerts
    this.slowEndpointAlerts = this.slowEndpointAlerts.filter(
      alert => alert.timestamp > cutoff
    );
  }
}

// Export singleton instance
export const performanceOptimizer = new PerformanceOptimizer();

// Export batch configurations for common use cases
export const batchConfigs = {
  // User data fetching
  userBatch: {
    batchKey: 'user-batch',
    maxBatchSize: 10,
    maxWaitTime: 100,
    requestTransformer: (requests: string[]) => ({ userIds: requests }),
    responseTransformer: (response: { users?: { id: string }[] }, requests: string[]) => 
      requests.map(id => response.users?.find((u: { id: string }) => u.id === id)),
  },
  
  // Establishment data fetching
  establishmentBatch: {
    batchKey: 'establishment-batch',
    maxBatchSize: 5,
    maxWaitTime: 150,
    requestTransformer: (requests: string[]) => ({ establishmentIds: requests }),
    responseTransformer: (response: { establishments?: { id: string }[] }, requests: string[]) =>
      requests.map(id => response.establishments?.find((e: { id: string }) => e.id === id)),
  },
  
  // Permission checking
  permissionBatch: {
    batchKey: 'permission-batch',
    maxBatchSize: 20,
    maxWaitTime: 50,
    requestTransformer: (requests: Array<{userId: string, permission: string}>) => ({ checks: requests }),
    responseTransformer: (response: { results?: boolean[] }, requests: Array<{userId: string, permission: string}>) =>
      requests.map((req, i) => response.results?.[i] || false),
  },
};

export default performanceOptimizer;
